import { useState } from "react"

import { useTranslation } from "react-i18next"

import { CheckCircle, ChevronDown, ChevronUp, HelpCircle, XCircle } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { cn } from "@/lib/utils"

const SAMPLE_RESULT = {
  confidence: 75,
  breakdown: {
    coverage: 32,
    alignment: 18,
    quality: 15,
    recency: 6,
    consistency: 4,
  },
  claims: [
    { id: "1", status: "supported" as const, text: "AI can process natural language", score: 92 },
    {
      id: "2",
      status: "unclear" as const,
      text: "Performance improves 50% year over year",
      score: 55,
    },
    {
      id: "3",
      status: "supported" as const,
      text: "Machine learning requires training data",
      score: 88,
    },
  ],
  sourcesUsed: [
    { title: "Company Documentation: AI Guidelines", kind: "library" },
    { title: "https://example.com/ai-research", kind: "url" },
    { title: "Internal Report Q4 2024", kind: "file" },
  ],
}

const breakdownConfig = [
  { key: "coverage" as const, max: 40, label: "Coverage" },
  { key: "alignment" as const, max: 25, label: "Alignment" },
  { key: "quality" as const, max: 20, label: "Quality" },
  { key: "recency" as const, max: 10, label: "Recency" },
  { key: "consistency" as const, max: 5, label: "Consistency" },
]

export function DemoFactCheckPanel() {
  const { t } = useTranslation()
  const [showBreakdown, setShowBreakdown] = useState(true)
  const [showAllClaims, setShowAllClaims] = useState(false)

  const getConfidenceColor = (confidence: number) => {
    if (confidence >= 80) return "text-success"
    if (confidence >= 50) return "text-warning-ink "
    return "text-destructive"
  }

  const getTrafficLightColor = (confidence: number) => {
    if (confidence >= 80) return "bg-success-ink"
    if (confidence >= 50) return "bg-warning-ink"
    return "bg-destructive-ink"
  }

  const getBarColor = (percentage: number) => {
    if (percentage >= 80) return "bg-success-ink"
    if (percentage >= 60) return "bg-warning-ink"
    if (percentage >= 40) return "bg-primary-ink"
    return "bg-destructive-ink"
  }

  const getStatusIcon = (status: "supported" | "contradicted" | "unclear") => {
    switch (status) {
      case "supported":
        return <CheckCircle className="text-success size-4" />
      case "contradicted":
        return <XCircle className="text-destructive size-4" />
      case "unclear":
        return <HelpCircle className="text-warning-ink size-4" />
    }
  }

  const supported = SAMPLE_RESULT.claims.filter((c) => c.status === "supported").length
  const unclear = SAMPLE_RESULT.claims.filter((c) => c.status === "unclear").length

  return (
    <div className="bg-background w-full space-y-4 rounded-lg border p-3 sm:max-w-md sm:p-4">
      <div className="flex items-center gap-2">
        <h3 className="font-semibold">{t("fact_check.panel_title")}</h3>
        <Badge className="bg-primary/20 text-primary border-none" variant="outline">
          BETA
        </Badge>
      </div>

      <div className="rounded-lg border p-4">
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold">{t("fact_check.overall_confidence")}</h4>
          <div className="flex items-center gap-2">
            <div
              className={cn(
                "border-foreground size-3 rounded-full border",
                getTrafficLightColor(SAMPLE_RESULT.confidence)
              )}
            />
            <span
              className={cn("text-3xl font-bold", getConfidenceColor(SAMPLE_RESULT.confidence))}
            >
              {SAMPLE_RESULT.confidence}%
            </span>
          </div>
        </div>

        <p className="text-muted-foreground mt-2 text-sm">{t("fact_check.confidence_moderate")}</p>

        <div className="mt-3 flex flex-wrap gap-2 text-xs">
          {supported > 0 && (
            <Badge className="border-success bg-success/15 text-foreground gap-1" variant="outline">
              <CheckCircle className="size-3" />
              {supported} Supported
            </Badge>
          )}
          {unclear > 0 && (
            <Badge
              className="border-warning-ink/40 bg-warning/30 text-warning-ink gap-1"
              variant="outline"
            >
              <HelpCircle className="size-3" />
              {unclear} Unclear
            </Badge>
          )}
        </div>

        <div className="mt-4">
          <Button
            className="w-full justify-between px-0 hover:bg-transparent"
            size="sm"
            variant="ghost"
            onClick={() => setShowBreakdown(!showBreakdown)}
          >
            <h5 className="text-muted-foreground text-xs font-medium">
              {t("fact_check.breakdown_title")}
            </h5>
            {showBreakdown ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
          </Button>

          {showBreakdown && (
            <div className="mt-3 space-y-3">
              {breakdownConfig.map(({ key, max, label }) => {
                const score = SAMPLE_RESULT.breakdown[key]
                const percentage = (score / max) * 100
                return (
                  <div key={key} className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium">{label}</span>
                      <span className="text-muted-foreground text-xs">
                        {score.toFixed(1)}/{max} {t("fact_check.points")}
                      </span>
                    </div>
                    <Progress
                      className="h-2"
                      indicatorClassName={getBarColor(percentage)}
                      value={percentage}
                    />
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      <div className="rounded-lg border p-4">
        <h4 className="mb-3 text-sm font-semibold">{t("fact_check.claims_analyzed")}</h4>
        <div className="space-y-3">
          {SAMPLE_RESULT.claims.slice(0, showAllClaims ? undefined : 2).map((claim) => (
            <div key={claim.id} className="rounded-md border p-3">
              <div className="flex items-start gap-2">
                {getStatusIcon(claim.status)}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium capitalize">{claim.status}</span>
                    <span className="text-muted-foreground text-xs">{claim.score}/100</span>
                  </div>
                  <p className="mt-1 text-sm break-words">{claim.text}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
        {SAMPLE_RESULT.claims.length > 2 && (
          <Button
            className="mt-3 w-full"
            size="sm"
            variant="ghost"
            onClick={() => setShowAllClaims(!showAllClaims)}
          >
            {showAllClaims
              ? t("fact_check.show_less")
              : `Show ${SAMPLE_RESULT.claims.length - 2} more`}
          </Button>
        )}
      </div>

      <div className="rounded-lg border p-4">
        <h4 className="mb-2 text-sm font-semibold">{t("fact_check.sources_checked")}</h4>
        <div className="space-y-2">
          {SAMPLE_RESULT.sourcesUsed.map((source, index) => (
            <div key={index} className="bg-muted/30 flex items-center gap-2 rounded-md p-2 text-sm">
              <span className="text-muted-foreground">•</span>
              <span className="flex-1 truncate text-xs">{source.title}</span>
              <Badge className="text-xs" variant="outline">
                {source.kind}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
